import React, { useState, useRef, useEffect, useMemo } from 'react';
import { SECTOR_GROUPS, parseSectors } from './sectors';


// Deterministic soft colour per sector label, for chips.
const CHIP_COLOURS = ['#5B3FD6', '#0B5BB5', '#056F4E', '#B25400', '#066576', '#A21C5A', '#3F4956', '#7A4D00'];
function chipColour(label) {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return CHIP_COLOURS[h % CHIP_COLOURS.length];
}

export default function SectorPicker({ value, onChange }) {
  const selected = useMemo(() => parseSectors(value), [value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggle(label) {
    const next = selectedSet.has(label) ? selected.filter(s => s !== label) : [...selected, label];
    onChange(next);
  }
  function remove(label) { onChange(selected.filter(s => s !== label)); }

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return SECTOR_GROUPS;
    return SECTOR_GROUPS
      .map(g => {
        const sectorHit = g.sector.toLowerCase().includes(q);
        const items = g.items.filter(i => i.toLowerCase().includes(q));
        if (sectorHit) return g; // whole group when the sector name matches
        if (items.length) return { sector: g.sector, items };
        return null;
      })
      .filter(Boolean);
  }, [q]);

  function toggleGroup(sector) { setOpenGroups(p => ({ ...p, [sector]: !p[sector] })); }

  return (
    <div className="secpick" ref={wrapRef}>
      <div className="secpick-field" onClick={() => setOpen(o => !o)}>
        {selected.length === 0 && <span className="secpick-ph">Select sectors…</span>}
        {selected.map(s => (
          <span key={s} className="secpick-chip" style={{ background: chipColour(s) + '1A', color: chipColour(s), borderColor: chipColour(s) + '40' }}>
            {s}
            <button type="button" className="secpick-chipx" onClick={(e) => { e.stopPropagation(); remove(s); }} aria-label={'Remove ' + s}>×</button>
          </span>
        ))}
        <span className="secpick-caret">{open ? '▴' : '▾'}</span>
      </div>

      {open && (
        <div className="secpick-panel">
          <div className="secpick-search">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search sectors and industries…"
            />
            {query && <button type="button" className="secpick-clear" onClick={() => setQuery('')}>×</button>}
          </div>
          <div className="secpick-list">
            {filtered.length === 0 && <div className="secpick-empty">No matches</div>}
            {filtered.map(g => {
              const expanded = q ? true : !!openGroups[g.sector];
              const sectorSel = selectedSet.has(g.sector);
              return (
                <div key={g.sector} className="secpick-group">
                  <div className="secpick-grouph">
                    <button type="button" className="secpick-grouptoggle" onClick={() => toggleGroup(g.sector)}>
                      {q ? '' : (expanded ? '▾' : '▸')}
                    </button>
                    <button
                      type="button"
                      className={'secpick-opt secpick-opt-sector' + (sectorSel ? ' is-sel' : '')}
                      onClick={() => toggle(g.sector)}
                    >
                      <span className="secpick-check">{sectorSel ? '✓' : ''}</span>
                      {g.sector}
                    </button>
                  </div>
                  {expanded && g.items.map(it => {
                    const sel = selectedSet.has(it);
                    return (
                      <button
                        key={it}
                        type="button"
                        className={'secpick-opt secpick-opt-item' + (sel ? ' is-sel' : '')}
                        onClick={() => toggle(it)}
                      >
                        <span className="secpick-check">{sel ? '✓' : ''}</span>
                        {it}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
